import axios from "axios";
import store from "../store";
import { tokenStorage } from "../utils/tokenStorage";

const DEFAULT_API_URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/apiv3`
    : "http://localhost:8129/apiv3";

const resolveApiUrl = () => {
  const envUrl = process.env.VUE_APP_API_URL;
  if (!envUrl) return DEFAULT_API_URL;
  if (/^https?:\/\//i.test(envUrl)) return envUrl;

  const normalizedPath = envUrl.startsWith("/") ? envUrl : `/${envUrl}`;
  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalizedPath}`;
  }
  return `http://localhost:8129${normalizedPath}`;
};

const api = axios.create({
  baseURL: resolveApiUrl(),
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const session = tokenStorage.getSession();
  if (session && session.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }
  return config;
});

let isRefreshing = false;
let pendingQueue = [];

const resolveQueue = (error, token) => {
  pendingQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  pendingQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (!response || response.status !== 401) {
      return Promise.reject(error);
    }

    if (config && config._retry) {
      return Promise.reject(error);
    }

    const session = tokenStorage.getSession();
    if (!session || !session.refreshToken) {
      store.dispatch("auth/logout");
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return api(config);
      });
    }

    config._retry = true;
    isRefreshing = true;

    try {
      const refreshed = await store.dispatch("auth/refresh");
      resolveQueue(null, refreshed.accessToken);
      config.headers.Authorization = `Bearer ${refreshed.accessToken}`;
      return api(config);
    } catch (refreshError) {
      resolveQueue(refreshError, null);
      store.dispatch("auth/logout");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
